import type {
  StaffPaymentException,
  StaffProviderHealthEntry,
  StaffResolvePaymentExceptionBody,
} from "@lafayette/schemas";
import type { IconName } from "../ui/icon";

// The words of the diagnostics pages. Every closed code of the contract has a word here, and `labels.test.ts` checks that no code
// is missing. A code that is not on a list is shown as the code itself, never as a guess.

type Provider = StaffProviderHealthEntry["provider"];
type Status = StaffProviderHealthEntry["status"];
type Mode = StaffProviderHealthEntry["mode"];
type LinkType = NonNullable<StaffProviderHealthEntry["recentFailures"][number]["link"]>["type"];

export const providerLabels: Readonly<Record<Provider, string>> = {
  stream: "Stream (chat)",
  trigger: "Trigger.dev (background jobs)",
  razorpay: "Razorpay (payments)",
  google: "Google (routes, places, weather)",
  expo: "Expo (push notifications)",
  model: "Model providers (AI assistant)",
  resend: "Resend (e-mail)",
  supabase: "Supabase (database and sign-in)",
};

export const statusLabels: Readonly<Record<Status, string>> = {
  NOT_CONFIGURED: "Not configured",
  FAKE: "Fake (not evidence)",
  UNKNOWN: "Unknown",
  OK: "OK",
  DEGRADED: "Degraded",
  DOWN: "Down",
};

/** An icon beside the word, so the state does not depend on colour (decoration, `aria-hidden`: the word is the state). Each status has its own. */
export const statusGlyphs: Readonly<Record<Status, IconName>> = {
  NOT_CONFIGURED: "circle",
  FAKE: "test-tube-01",
  UNKNOWN: "help-circle",
  OK: "checkmark-circle-02",
  DEGRADED: "alert-02",
  DOWN: "cancel-circle",
};

export const statusHints: Readonly<Record<Status, string>> = {
  NOT_CONFIGURED: "The provider is off or not set up. Nothing runs.",
  FAKE: "A fake stands in. Its result is never evidence.",
  UNKNOWN: "No data, or the source failed. This is not a green light.",
  OK: "Live, with data, and the error rate is low.",
  DEGRADED: "The error rate is 10% or more, a task is paused, or the queue is stale.",
  DOWN: "The error rate is 50% or more, or the queue has been stale for 30 minutes or more.",
};

export const modeLabels: Readonly<Record<Mode, string>> = {
  off: "Off",
  not_configured: "Not configured",
  fake: "Fake",
  live: "Live",
};

/** The closed `note` codes of provider health. */
export const noteLabels: Readonly<Record<string, string>> = {
  FAKE_NOT_EVIDENCE: "A fake result is never evidence. No count is shown.",
  DB_PROBE_ONLY: "This checks the database only, not sign-in or file storage.",
  QUEUE_STALE: "The job queue is stale: jobs that are due are not being claimed.",
  PAUSED: "A task is paused.",
  SOURCE_FAILED: "The data source failed. This is not a green light.",
};

export const linkTypeLabels: Readonly<Record<LinkType, string>> = {
  payment_request: "Payment",
  background_intent: "Job",
  agent_run: "AI run",
  conversation: "Conversation",
  import_job: "Import",
};

/**
 * The evidence label of a payment result (the payments contract): `FAKE` for the fake gateway, `REAL` for the real provider, `NONE` when no
 * provider is set up. The answer of provider health carries the mode of the Razorpay row; this turns it into the label.
 */
export function paymentEvidenceLabel(mode: Mode): "FAKE" | "REAL" | "NONE" {
  return mode === "fake" ? "FAKE" : mode === "live" ? "REAL" : "NONE";
}

export const exceptionKindLabels: Readonly<Record<StaffPaymentException["kind"], string>> = {
  DUPLICATE_CONFLICT: "Two different events for the same payment",
  UNKNOWN_ORDER: "A payment for an order Lafayette does not know",
  ORDER_MISMATCH: "The order does not match the request",
  CUSTOMER_MISMATCH: "The customer does not match",
  AMOUNT_MISMATCH: "The amount does not match",
  CURRENCY_MISMATCH: "The currency does not match",
  LATE_SETTLEMENT: "Money arrived after the request ended",
  REQUEST_WITHDRAWN: "Money arrived for a withdrawn offer",
  UNEXPECTED_STATE: "The payment was in an unexpected state",
  UNKNOWN_REFUND: "A refund Lafayette did not ask for",
  REFUND_MISMATCH: "The refund does not match",
};

type ResolutionCode = StaffResolvePaymentExceptionBody["resolutionCode"];
export const resolutionLabels: Readonly<Record<ResolutionCode, string>> = {
  REFUNDED_OUT_OF_BAND: "The money was refunded in the provider dashboard",
  CONFIRMED_NO_MONEY_MOVED: "I checked: no money moved",
  ADOPTED_REFUND: "The refund is real and correct: adopt it",
  DUPLICATE_IGNORED: "It is a duplicate of an event already handled",
  OTHER: "Another decision (explain in the reason)",
};

/** The closed `reasonCode`s of a failed delivery (`deliveryFailureCodes` of the domain) and a short plain meaning. */
export const deliveryReasonLabels: Readonly<Record<string, string>> = {
  RETRY_EXHAUSTED: "The provider failed on every attempt",
  PROVIDER_REJECTED: "The provider refused the message",
  PAYLOAD_REFUSED: "The message failed the payload rules",
  LINK_UNAVAILABLE: "The record the message links to cannot be opened",
  RESULT_UNKNOWN: "The provider may have taken the message; it was not sent again",
  // CHT-764 review fix 8: a message that Expo took (SENT), and whose receipt is not good.
  RECEIPT_ERROR: "Expo took the message, but its receipt reports an error",
  RECEIPT_MISSING: "Expo took the message, but no receipt came",
};

/** The closed receipt failure codes of a notification that Expo took (CHT-764 review fix 8). */
export const receiptCodeLabels: Readonly<Record<string, string>> = {
  DEVICE_NOT_REGISTERED: "The device is no longer registered; its token was ended",
  MESSAGE_TOO_BIG: "The message was too big for the device",
  MESSAGE_RATE_EXCEEDED: "Too many messages to the device",
  INVALID_CREDENTIALS: "The push credentials were refused",
  MISMATCH_SENDER_ID: "The push sender does not match the device",
  MISSING: "No receipt came for a device",
  OTHER: "Another receipt error",
};

/** The action families of the audit log, for the filter. A person may also type any other action or family. */
export const auditFamilies: readonly Readonly<{ value: string; label: string }>[] = [
  { value: "staff_role", label: "Roles granted or revoked" },
  { value: "staff_user", label: "Staff accounts (offboarding, reactivation)" },
  { value: "staff_availability", label: "Staff unavailability" },
  { value: "concierge_assignment", label: "Cover and assignments" },
  { value: "handoff", label: "Handoffs" },
  { value: "concierge_request", label: "Requests" },
  { value: "payment_request", label: "Payment requests" },
  { value: "payment_refund", label: "Refunds" },
  { value: "payment_reconciliation", label: "Payment reconciliation" },
  { value: "payment_quote", label: "Payment quotes" },
  { value: "customer_memory_fact", label: "Customer memory" },
  { value: "customer", label: "Customer data and privacy" },
  { value: "background_job", label: "Job re-runs, pauses and resumes" },
  { value: "import_job", label: "Catalog imports" },
  { value: "catalog_item", label: "Catalog items" },
  { value: "notification", label: "Notifications" },
  { value: "request_sla", label: "Request SLA" },
];

export const auditActorKindLabels = {
  customer: "Customer",
  staff: "Staff",
  system: "System",
} as const;
